import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimeSubscription
 * Manages a Supabase real-time channel with retry logic, cleanup, and connection status.
 *
 * @param {string} channelName - Unique channel identifier
 * @param {Object} config - { table, schema, event, filter }
 * @param {Function} onPayload - Callback receiving the realtime payload
 * @param {Object} options - { enabled, maxRetries, retryDelay }
 */
export function useRealtimeSubscription(
  channelName,
  config,
  onPayload,
  options = {}
) {
  const { enabled = true, maxRetries = 5, retryDelay = 3000 } = options;
  const [status, setStatus] = useState('idle'); // idle | connecting | connected | error | closed
  const channelRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (retryTimerRef?.current) {
      clearTimeout(retryTimerRef?.current);
      retryTimerRef.current = null;
    }
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }
  }, []);

  const subscribe = useCallback(() => {
    if (!mountedRef?.current || !enabled) return;

    cleanup();
    setStatus('connecting');

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: config?.event || '*',
          schema: config?.schema || 'public',
          table: config?.table,
          ...(config?.filter ? { filter: config?.filter } : {}),
        },
        (payload) => {
          if (mountedRef?.current) {
            onPayload?.(payload);
          }
        }
      )?.subscribe((subscriptionStatus, err) => {
        if (!mountedRef?.current) return;

        if (subscriptionStatus === 'SUBSCRIBED') {
          setStatus('connected');
          retryCountRef.current = 0;
        } else if (
          subscriptionStatus === 'CHANNEL_ERROR' ||
          subscriptionStatus === 'TIMED_OUT' ||
          subscriptionStatus === 'CLOSED'
        ) {
          setStatus('error');
          console.warn(`[Realtime] ${channelName} ${subscriptionStatus}`, err);

          if (retryCountRef?.current < maxRetries) {
            retryCountRef.current += 1;
            const delay = retryDelay * Math.pow(1.5, retryCountRef?.current - 1);
            retryTimerRef.current = setTimeout(() => {
              if (mountedRef?.current) subscribe();
            }, delay);
          } else {
            setStatus('closed');
            console.error(`[Realtime] ${channelName} max retries reached`);
          }
        }
      });

    channelRef.current = channel;
  }, [channelName, config?.table, config?.event, config?.filter, enabled, maxRetries, retryDelay]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) subscribe();

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [subscribe, enabled]);

  return { status };
}

/**
 * useMultipleRealtimeSubscriptions
 * Manages multiple real-time subscriptions with shared cleanup.
 *
 * @param {Array} subscriptions - Array of { channelName, config, onPayload, options }
 */
export function useMultipleRealtimeSubscriptions(subscriptions = []) {
  const [statuses, setStatuses] = useState({});
  const channelsRef = useRef({});
  const retryTimersRef = useRef({});
  const mountedRef = useRef(true);

  const cleanupChannel = useCallback((name) => {
    if (retryTimersRef?.current?.[name]) {
      clearTimeout(retryTimersRef?.current?.[name]);
      delete retryTimersRef?.current?.[name];
    }
    if (channelsRef?.current?.[name]) {
      supabase?.removeChannel(channelsRef?.current?.[name]);
      delete channelsRef?.current?.[name];
    }
  }, []);

  const subscribeOne = useCallback((sub) => {
    const { channelName, config, onPayload, options = {} } = sub;
    const { maxRetries = 5, retryDelay = 3000 } = options;
    let retryCount = 0;

    cleanupChannel(channelName);

    setStatuses(prev => ({ ...prev, [channelName]: 'connecting' }));

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: config?.event || '*',
          schema: config?.schema || 'public',
          table: config?.table,
          ...(config?.filter ? { filter: config?.filter } : {}),
        },
        (payload) => {
          if (mountedRef?.current) onPayload?.(payload);
        }
      )?.subscribe((subscriptionStatus, err) => {
        if (!mountedRef?.current) return;

        if (subscriptionStatus === 'SUBSCRIBED') {
          setStatuses(prev => ({ ...prev, [channelName]: 'connected' }));
          retryCount = 0;
        } else if (
          subscriptionStatus === 'CHANNEL_ERROR' ||
          subscriptionStatus === 'TIMED_OUT' ||
          subscriptionStatus === 'CLOSED'
        ) {
          setStatuses(prev => ({ ...prev, [channelName]: 'error' }));
          console.warn(`[Realtime] ${channelName} ${subscriptionStatus}`, err);

          if (retryCount < maxRetries) {
            retryCount += 1;
            const delay = retryDelay * Math.pow(1.5, retryCount - 1);
            retryTimersRef.current[channelName] = setTimeout(() => {
              if (mountedRef?.current) subscribeOne(sub);
            }, delay);
          } else {
            setStatuses(prev => ({ ...prev, [channelName]: 'closed' }));
          }
        }
      });

    channelsRef.current[channelName] = channel;
  }, [cleanupChannel]);

  useEffect(() => {
    mountedRef.current = true;
    subscriptions?.forEach(sub => subscribeOne(sub));

    return () => {
      mountedRef.current = false;
      Object.keys(channelsRef?.current)?.forEach(name => cleanupChannel(name));
    };
  }, []);

  const isAllConnected = Object.values(statuses)?.every(s => s === 'connected');
  const hasError = Object.values(statuses)?.some(s => s === 'error' || s === 'closed');

  return { statuses, isAllConnected, hasError };
}
